import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { UpsellingBanner as BaseBanner } from '@factorialco/f0-react'
import { ReferralProgramPoliciesDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import useLocalStorage from 'modules/core/lib/useLocalStorage'

import ReferralBrazil from 'modules/referral_program/lib/assets/referral_brazil.png'
import BannerImage from 'modules/referral_program/lib/assets/iphone_air.png'

const STORAGE_KEY = 'referral_program_banner_state'

interface BannerState {
  closedAt?: number
  permanentlyDismissed?: boolean
}

const ReferalProgramBanner: React.FC = () => {
  const [bannerState, setBannerState] = useLocalStorage<BannerState>(STORAGE_KEY, {})
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company, canRequestUpsell } = useCurrentSession()
  const { data: policies } = useQuery(ReferralProgramPoliciesDocument)
  const canSeeReferralPage = policies?.apiCore._policies.seeReferralProgramPage.can
  const isBrazil = company.legalEntities.find((e) => e.main)?.country === 'br'

  const shouldShow = React.useMemo(() => {
    if (bannerState?.permanentlyDismissed) {
      return false
    }
    if (!bannerState?.closedAt) {
      return true
    }

    const now = Date.now()
    const daysSinceClosed = (now - bannerState.closedAt) / (24 * 60 * 60 * 1000)
    return daysSinceClosed >= 15
  }, [bannerState])

  const handleClose = () => {
    track(trackingEvents.settings.dimissSettingsReferralBanner)
    if (bannerState?.closedAt) {
      setBannerState({
        ...bannerState,
        permanentlyDismissed: true,
      })
    } else {
      setBannerState({
        ...bannerState,
        closedAt: Date.now(),
      })
    }
  }

  if (!shouldShow || !canSeeReferralPage) {
    return null
  }

  return (
    <Box paddingX="s48" paddingY="s12" paddingBottom="s24">
      <BaseBanner
        title={
          isBrazil
            ? i18n.t(
                'company_settings.general.factorial_referral_program.announcement_banner.title',
                {
                  amount: 'R$300',
                  max_discount: 'R$3000',
                }
              )
            : i18n.t('company_settings.general.factorial_referral_program.iphone_air.title_banner')
        }
        mediaUrl={isBrazil ? ReferralBrazil : BannerImage}
        subtitle={
          canRequestUpsell
            ? isBrazil
              ? i18n.t(
                  'company_settings.general.factorial_referral_program.announcement_banner.subtitle',
                  {
                    amount: 'R$300',
                    max_discount: 'R$3000',
                  }
                )
              : i18n.t(
                  'company_settings.general.factorial_referral_program.iphone_air.subtitle_banner'
                )
            : isBrazil
              ? i18n.t(
                  'company_settings.general.factorial_referral_program.announcement_banner.subtitle_employee',
                  {
                    amount: 'R$300',
                    max_discount: 'R$3000',
                  }
                )
              : i18n.t(
                  'company_settings.general.factorial_referral_program.iphone_air.subtitle_banner_employee'
                )
        }
        primaryAction={{
          label: i18n.t(
            'company_settings.general.factorial_referral_program.announcement_banner.primary_action'
          ),
          onClick: () => {
            track(trackingEvents.settings.clickSettingsReferralBanner)
            navigate(navigation.settings.referralProgram.fullPath())
          },
          variant: 'default',
        }}
        onClose={handleClose}
      />
    </Box>
  )
}

export default ReferalProgramBanner
